import { useId } from 'react';
import { motion } from 'motion/react';
import { spring } from '../motion/presets';
import { cx } from '../internal/cx';

export type SwitchProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Libellé visible, lié au contrôle. */
  label?: string;
  disabled?: boolean;
  className?: string;
  /** Libellé accessible si pas de `label` visible. */
  'aria-label'?: string;
};

/**
 * Interrupteur `role="switch"` (Espace/Entrée pour basculer), pouce animé.
 */
export function Switch({
  checked,
  onChange,
  label,
  disabled,
  className,
  'aria-label': ariaLabel,
}: SwitchProps) {
  const id = useId();
  return (
    <span className={cx('inline-flex items-center gap-2.5', className)}>
      <button
        type="button"
        role="switch"
        id={id}
        aria-checked={checked}
        aria-label={label ? undefined : ariaLabel}
        aria-labelledby={label ? `${id}-label` : undefined}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cx(
          'relative inline-flex h-7 w-12 shrink-0 items-center rounded-full border px-0.5',
          'transition-colors duration-fast ease-fun disabled:cursor-not-allowed disabled:opacity-50',
          checked ? 'border-panda-500 bg-panda-500' : 'border-ink-600 bg-ink-700',
        )}
      >
        <motion.span
          aria-hidden="true"
          layout
          transition={spring.snappy}
          className={cx(
            'block h-5 w-5 rounded-full shadow-card',
            checked ? 'ml-auto bg-ink-950' : 'bg-ink-100',
          )}
        />
      </button>
      {label && (
        <label id={`${id}-label`} htmlFor={id} className="cursor-pointer text-sm text-ink-100">
          {label}
        </label>
      )}
    </span>
  );
}
